/**
 * Grant / revoke dialog.
 *
 * Two stages, deliberately. Stage 1 resolves and DISPLAYS the target identity
 * for explicit confirmation; stage 2 is the step-up re-proof. The confirmation
 * step is not ceremony — `GET /v1/users/search` returns usernames without ids by
 * design, so a resolution step is required anyway, and showing the resolved
 * email and current roles is what stops a grant landing on a look-alike handle.
 *
 * SECURITY: the mutation submits through `stepUpFetch`, never the traced `api`
 * client. The body carries the acting admin's password and TOTP code, and
 * `api.ts` writes every request into the telemetry apiTraces ring buffer that
 * `window.fiasDebug.getState()` and the report-a-problem dialog expose.
 */

import { useEffect, useState } from 'react';
import { Loader2 } from 'lucide-react';
import { api, type AdminUserLookupResult } from '@/lib/api';
import { stepUpFetch } from '@/lib/step-up-fetch';
import { StepUpDialog, type StepUpCredentials } from '@/auth/components/StepUpDialog';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';

export type RoleMutationMode = 'grant' | 'revoke';

interface RoleMutationDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  mode: RoleMutationMode;
  role: string;
  roleLabel: string;
  /** Pre-resolved target — set when revoking from a row in the members table. */
  presetUser?: { userId: string; username: string | null };
  onSuccess: () => void;
}

export function RoleMutationDialog({
  open,
  onOpenChange,
  mode,
  role,
  roleLabel,
  presetUser,
  onSuccess,
}: RoleMutationDialogProps) {
  const [query, setQuery] = useState('');
  const [resolved, setResolved] = useState<AdminUserLookupResult | null>(null);
  const [reason, setReason] = useState('');
  const [error, setError] = useState('');
  const [resolving, setResolving] = useState(false);
  const [stepUpOpen, setStepUpOpen] = useState(false);

  useEffect(() => {
    if (!open) return;
    setQuery(presetUser?.username ?? '');
    setResolved(null);
    setReason('');
    setError('');
    setResolving(false);
    setStepUpOpen(false);
  }, [open, presetUser]);

  /**
   * Accepts a username, an email, or a raw userId — whichever the admin has.
   * `adminLookupUserByAnyIdentifier` already owns that shape detection.
   */
  const resolve = async () => {
    const value = query.trim().replace(/^@/, '');
    if (!value) return;
    setResolving(true);
    setError('');
    try {
      setResolved(await api.adminLookupUserByAnyIdentifier(value));
    } catch (err) {
      setResolved(null);
      setError(err instanceof Error ? err.message : 'Could not find that user.');
    } finally {
      setResolving(false);
    }
  };

  const submit = async (creds: StepUpCredentials) => {
    if (!resolved) return;
    const path =
      mode === 'grant'
        ? `/admin/roles/${encodeURIComponent(role)}/grants`
        : `/admin/roles/${encodeURIComponent(role)}/revocations`;

    await stepUpFetch(path, {
      method: 'POST',
      body: {
        userId: resolved.userId,
        reason: reason.trim(),
        currentPassword: creds.currentPassword,
        ...(creds.totpCode ? { totpCode: creds.totpCode } : {}),
      },
    });
  };

  const alreadyHasRole = resolved?.roles?.includes(role) ?? false;
  const canProceed =
    resolved !== null &&
    reason.trim().length > 0 &&
    (mode === 'grant' ? !alreadyHasRole : alreadyHasRole);

  return (
    <>
      <Dialog open={open && !stepUpOpen} onOpenChange={onOpenChange}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {mode === 'grant' ? 'Grant' : 'Revoke'} {roleLabel}
            </DialogTitle>
            <DialogDescription>
              {mode === 'grant'
                ? 'Confirm exactly who this is before granting. You will be asked to re-enter your password.'
                : 'Confirm exactly who this is before revoking. You will be asked to re-enter your password.'}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="role-target">Username, email, or user ID</Label>
              <div className="flex gap-2">
                <Input
                  id="role-target"
                  value={query}
                  onChange={(e) => {
                    setQuery(e.target.value);
                    setResolved(null);
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      void resolve();
                    }
                  }}
                  placeholder="jake, jake@example.com, or usr_…"
                  autoFocus
                  disabled={resolving}
                />
                <Button
                  type="button"
                  variant="secondary"
                  onClick={() => void resolve()}
                  disabled={resolving || !query.trim()}
                >
                  {resolving && <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />}
                  Find
                </Button>
              </div>
            </div>

            {error && <p className="text-sm text-destructive">{error}</p>}

            {/* The confirm-the-target step. */}
            {resolved && (
              <div className="space-y-2 rounded-md border border-border bg-muted/40 p-3 text-sm">
                <div className="flex items-center justify-between gap-2">
                  <span className="font-medium text-foreground">{resolved.username}</span>
                  <Badge variant={resolved.status === 'active' ? 'secondary' : 'destructive'}>
                    {resolved.status}
                  </Badge>
                </div>
                {resolved.displayName && (
                  <p className="text-muted-foreground">{resolved.displayName}</p>
                )}
                <p className="text-muted-foreground">{resolved.email ?? 'no email on file'}</p>
                <p className="font-mono text-xs text-muted-foreground">{resolved.userId}</p>
                <p className="text-muted-foreground">
                  Current roles:{' '}
                  <span className="text-foreground">{resolved.roles.join(', ')}</span>
                </p>

                {mode === 'grant' && alreadyHasRole && (
                  <p className="text-amber-600 dark:text-amber-500">
                    This user already has {roleLabel}. Nothing to do.
                  </p>
                )}
                {mode === 'revoke' && !alreadyHasRole && (
                  <p className="text-amber-600 dark:text-amber-500">
                    This user does not have {roleLabel}. Nothing to do.
                  </p>
                )}
              </div>
            )}

            <div className="space-y-2">
              <Label htmlFor="role-reason">Reason</Label>
              <Input
                id="role-reason"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="Ticket number or short justification"
                maxLength={500}
              />
              <p className="text-xs text-muted-foreground">
                Recorded in the audit log alongside your account.
              </p>
            </div>
          </div>

          <DialogFooter>
            <Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button
              type="button"
              variant={mode === 'revoke' ? 'destructive' : 'default'}
              disabled={!canProceed}
              onClick={() => setStepUpOpen(true)}
            >
              Continue
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <StepUpDialog
        open={stepUpOpen}
        onOpenChange={setStepUpOpen}
        title={`${mode === 'grant' ? 'Grant' : 'Revoke'} ${roleLabel}`}
        description={
          mode === 'grant'
            ? `Confirm it’s you to grant ${roleLabel} to ${resolved?.username ?? 'this user'}.`
            : `Confirm it’s you to revoke ${roleLabel} from ${resolved?.username ?? 'this user'}.`
        }
        confirmLabel={mode === 'grant' ? 'Grant role' : 'Revoke role'}
        destructive={mode === 'revoke'}
        onConfirm={submit}
        onSuccess={() => {
          onOpenChange(false);
          onSuccess();
        }}
      />
    </>
  );
}
